import { Fragment, useEffect, useState } from 'react';
import {
  MdOutlineAccountBalanceWallet,
  MdOutlinePendingActions,
  MdOutlineStorefront,
} from 'react-icons/md';
import {
  FiChevronDown,
  FiDownload,
  FiCalendar,
  FiSearch,
} from 'react-icons/fi';
import history from 'src/browser/history';
import { propertyUrl } from 'src/utils/pageUrls';
import {
  getAdvisorDateTimestamp,
  getSignedOrderAmount,
} from '../advisorClientDetailData';
import AdvisorTableEmptyState from '../AdvisorTableEmptyState';
import { useClientDetail } from './ClientDetailContext';
import {
  downloadCsv,
  formatCompactDateTime,
  formatCount,
  formatCurrency,
  formatDateTime,
  formatShortId,
  formatStatus,
  isWithinDays,
} from './helpers';
import {
  Card,
  CardHeader,
  CardTitle,
  DataFooter,
  DataSummaryCard,
  DataSummaryGrid,
  DataSummaryIcon,
  DataSummaryValue,
  DataTable,
  DataTableScroll,
  DataToolbar,
  DetailLabel,
  FilterSelect,
  OrdersCreated,
  OrdersDateFilter,
  OrdersExport,
  OrdersLayout,
  OrdersSelectField,
  PageInfo,
  ReferenceText,
  SearchField,
  StatusPill,
  TransactionAmount,
} from './styles';
import { OrdersPropertyCode, PaginationControls } from './components';
import { useActivityLayout } from './hooks';

const ORDERS_PAGE_SIZE = 10;

const OrdersTab = () => {
  const { detail, properties, propertiesLoading } = useClientDetail();
  const { containerRef, summaryLayout, filterLayout } = useActivityLayout();
  const [search, setSearch] = useState('');
  const [orderType, setOrderType] = useState('all');
  const [propertyCode, setPropertyCode] = useState('all');
  const [days, setDays] = useState('all');
  const [page, setPage] = useState(1);
  const orders = detail.orders || [];
  const normalizedSearch = search.trim().toLowerCase();
  const propertyCodes = Array.from(
    new Set(orders.map((order) => order.propertyCode).filter(Boolean)),
  ).sort();
  const filteredOrders = orders
    .filter((order) =>
      [order.id, order.propertyCode].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      ),
    )
    .filter((order) => orderType === 'all' || order.side === orderType)
    .filter(
      (order) => propertyCode === 'all' || order.propertyCode === propertyCode,
    )
    .filter((order) => days === 'all' || isWithinDays(order.date, days))
    .sort(
      (left, right) =>
        getAdvisorDateTimestamp(right.date) -
        getAdvisorDateTimestamp(left.date),
    );
  const totalPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / ORDERS_PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * ORDERS_PAGE_SIZE;
  const visibleOrders = filteredOrders.slice(
    firstIndex,
    firstIndex + ORDERS_PAGE_SIZE,
  );

  useEffect(() => setPage(1), [search, orderType, propertyCode, days]);

  const exportOrders = () => {
    const rows = [
      [
        'Order ID',
        'Investment',
        'Type',
        'Qty',
        'Price / Brick',
        'Net Amount',
        'Created',
        'Status',
      ],
      ...filteredOrders.map((order) => {
        const netAmount = getSignedOrderAmount(order);
        return [
          order.id,
          order.propertyCode,
          formatStatus(order.side),
          order.quantity,
          order.price,
          netAmount !== null ? netAmount.toFixed(2) : null,
          order.date,
          'Pending',
        ];
      }),
    ];
    downloadCsv('client-orders.csv', rows);
  };

  const summary = [
    {
      Icon: MdOutlinePendingActions,
      label: 'Pending Orders',
      value: formatCount(detail.pendingOrderCount),
    },
    {
      Icon: MdOutlineStorefront,
      label: 'Bricks for Sale',
      value: formatCount(detail.pendingSellBricks),
    },
    {
      Icon: MdOutlineAccountBalanceWallet,
      label: 'Funds Reserved',
      value: formatCurrency(detail.pendingFundsHeld),
    },
  ];

  return (
    <OrdersLayout
      ref={containerRef}
      data-summary-layout={summaryLayout}
      data-filter-layout={filterLayout}
    >
      <Card>
        <CardHeader>
          <CardTitle>Pending Orders</CardTitle>
          <OrdersExport
            type="button"
            disabled={!filteredOrders.length}
            onClick={exportOrders}
          >
            <FiDownload aria-hidden="true" />
            Export
          </OrdersExport>
        </CardHeader>
        <DataSummaryGrid>
          {summary.map(({ Icon, label, value }) => (
            <DataSummaryCard key={label}>
              <DataSummaryIcon>
                <Icon aria-hidden="true" />
              </DataSummaryIcon>
              <div>
                <DetailLabel>{label}</DetailLabel>
                <DataSummaryValue>{value}</DataSummaryValue>
              </div>
            </DataSummaryCard>
          ))}
        </DataSummaryGrid>
        <DataToolbar>
          <SearchField>
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder="Search orders..."
              aria-label="Search client orders"
              onChange={(event) => setSearch(event.target.value)}
            />
          </SearchField>
          <OrdersSelectField>
            <FilterSelect
              value={orderType}
              aria-label="Filter orders by type"
              onChange={(event) => setOrderType(event.target.value)}
            >
              <option value="all">All types</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
              <option value="pre">Pre</option>
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
          <OrdersSelectField>
            <FilterSelect
              value={propertyCode}
              aria-label="Filter orders by investment"
              onChange={(event) => setPropertyCode(event.target.value)}
            >
              <option value="all">All investments</option>
              {propertyCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
          <OrdersDateFilter>
            <FiCalendar aria-hidden="true" />
            <FilterSelect
              value={days}
              aria-label="Filter orders by date"
              onChange={(event) => setDays(event.target.value)}
            >
              <option value="all">All time</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersDateFilter>
        </DataToolbar>
        {visibleOrders.length ? (
          <Fragment>
            <DataTableScroll>
              <DataTable>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Investment</th>
                    <th>Type</th>
                    <th className="numeric">Qty</th>
                    <th className="numeric">Price / Brick</th>
                    <th
                      className="numeric"
                      title="Sale proceeds after fees or purchase costs including fees"
                    >
                      Net Amount
                    </th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order, index) => (
                    <tr
                      key={order.id || index}
                      tabIndex={0}
                      onClick={(event) => {
                        if (event.target.closest('a')) return;
                        history.push(propertyUrl(order.propertyCode));
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          history.push(propertyUrl(order.propertyCode));
                        }
                      }}
                    >
                      <td>
                        <ReferenceText title={order.id}>
                          {formatShortId(order.id)}
                        </ReferenceText>
                      </td>
                      <td>
                        <OrdersPropertyCode
                          order={order}
                          properties={properties}
                          propertiesLoading={propertiesLoading}
                        />
                      </td>
                      <td>
                        <StatusPill
                          className={
                            { buy: 'positive', sell: 'negative', pre: 'info' }[
                              order.side
                            ]
                          }
                        >
                          {formatStatus(order.side)}
                        </StatusPill>
                      </td>
                      <td className="numeric">{formatCount(order.quantity)}</td>
                      <td className="numeric">{formatCurrency(order.price)}</td>
                      <td className="numeric">
                        <TransactionAmount
                          className={
                            {
                              sell: 'positive',
                              buy: 'negative',
                              pre: 'negative',
                            }[order.side]
                          }
                          title={
                            order.side === 'sell'
                              ? 'Amount received after transaction fees'
                              : 'Amount paid including transaction fees'
                          }
                        >
                          {formatCurrency(getSignedOrderAmount(order))}
                        </TransactionAmount>
                      </td>
                      <td>
                        <OrdersCreated title={formatDateTime(order.date)}>
                          {formatCompactDateTime(order.date)}
                        </OrdersCreated>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </DataTableScroll>
            <DataFooter>
              <PageInfo>
                {`Showing ${firstIndex + 1} to ${Math.min(
                  firstIndex + ORDERS_PAGE_SIZE,
                  filteredOrders.length,
                )} of ${filteredOrders.length} orders`}
              </PageInfo>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                label="Order pages"
                onChange={setPage}
              />
            </DataFooter>
          </Fragment>
        ) : (
          <DataTableScroll>
            <AdvisorTableEmptyState>
              {detail.orders === null
                ? 'Pending orders are currently unavailable.'
                : orders.length === 0
                  ? 'No pending orders.'
                  : 'No orders match the selected filters.'}
            </AdvisorTableEmptyState>
          </DataTableScroll>
        )}
      </Card>
    </OrdersLayout>
  );
};

export default OrdersTab;
